import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import Label from "components/Input/Label/Label";
import HelperMessage from "components/Input/HelperMessage/HelperMessage";
import { outlinedInputSx } from "lib/muiStyle/muiStyle";

const defaultOptions = [];

const AsyncAutocomplete = ({
  fetchOptions,
  value,
  onChange,
  getOptionLabel,
  isOptionEqualToValue,
  label,
  placeholder,
  preloadedOptions = defaultOptions,
  useLocalFilter = false,
  hasMoreOptions = true,
  numericExactMatch = false,
  localFilterFn,
  disabled = false,
  required = false,
  error = false,
  errorMessage = "",
  noOptionsText,
  renderOption,
  sx = {},
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(preloadedOptions);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [focus, setFocus] = useState(false);

  const skipSearch = useRef(false);

  // Sync options with preloadedOptions when input is empty
  useEffect(() => {
    if (inputValue === "") {
      setOptions(preloadedOptions);
    }
  }, [preloadedOptions, inputValue]);
  
  useEffect(() => {
    let active = true;

    if (inputValue === "") {
      setOptions(preloadedOptions.length > 0 ? preloadedOptions : []);
      setLoading(false);
      return undefined;
    }

    if (skipSearch.current) {
      skipSearch.current = false;
      setLoading(false);
      return undefined;
    }

    const localFilterEnabled = useLocalFilter && preloadedOptions.length > 0;
    if (localFilterEnabled) {
      const trimmed = (inputValue || "").trim();
      const lower = trimmed.toLowerCase();
      const isNumericSearch = numericExactMatch && /^\d+$/.test(trimmed);
      let hasExactNumericMatch = false;

      const localResults = localFilterFn
        ? localFilterFn(preloadedOptions, inputValue)
        : preloadedOptions.filter((opt) => {
            const label = String(
              getOptionLabel
                ? getOptionLabel(opt)
                : opt.title || opt.label || "",
            );
            if (isNumericSearch) {
              const value = String(opt?.value ?? "");
              const key = String(opt?.key ?? "");
              const isExact =
                value === trimmed ||
                key === trimmed ||
                label.startsWith(`${trimmed} -`);
              if (isExact) hasExactNumericMatch = true;
            }
            return label.toLowerCase().includes(lower);
          });
      if (localResults.length > 0) {
        setOptions(localResults);
        const shouldReturn =
          !fetchOptions ||
          hasMoreOptions === false ||
          !isNumericSearch ||
          hasExactNumericMatch;
        if (shouldReturn) {
          setLoading(false);
          return undefined;
        }
      } else if (hasMoreOptions === false || !fetchOptions) {
        setOptions([]);
        setLoading(false);
        return undefined;
      }
    }

    if (!fetchOptions) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }
    
    setLoading(true);
    const timer = setTimeout(async () => {
      if (fetchOptions) {
        try {
          const results = await fetchOptions(inputValue);
          if (active) {
            setOptions(results);
          }
        } catch (err) {
          console.error(err);
          if (active) setOptions([]);
        }
      }
      if (active) setLoading(false);
    }, 500);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [
    inputValue,
    fetchOptions,
    preloadedOptions,
    getOptionLabel,
    useLocalFilter,
    hasMoreOptions,
    numericExactMatch,
    localFilterFn,
  ]);

  return (
    <div style={{ ...sx }}>
      {label && <Label label={label} mandatory={required} />}
      <Autocomplete
        size="small"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        componentsProps={{
            popper: {
              placement: "bottom-start",
              modifiers: [
                {
                  name: "flip",
                  enabled: false,
                },
              ],
            },
        }}
        isOptionEqualToValue={isOptionEqualToValue}
        getOptionLabel={getOptionLabel || ((option) => option.title || option.label || "")}
        options={options}
        loading={loading}
        onChange={onChange}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "reset") {
              skipSearch.current = true;
          }
          setInputValue(newInputValue);
        }}
        value={value}
        renderOption={
          renderOption
            ? (props, option) => renderOption(props, option)
            : undefined
        }
        renderInput={(params) => (
          <TextField 
            {...params} 
            placeholder={placeholder}
            onFocus={(e) => {
              setFocus(true);
              if (params.inputProps.onFocus) params.inputProps.onFocus(e);
            }}
            onBlur={(e) => {
              setFocus(false);
              if (params.inputProps.onBlur) params.inputProps.onBlur(e);
            }}
            InputProps={{
              ...params.InputProps,
              sx: outlinedInputSx(focus, error, false),
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            size="small"
            hiddenLabel
          />
        )}
        disabled={disabled}
        fullWidth
        noOptionsText={
          noOptionsText ||
          (inputValue
            ? t("component.asyncAutocomplete.noResults")
            : t("component.asyncAutocomplete.typeToSearch"))
        }
      />
      <HelperMessage message={error && errorMessage} />
    </div>
  );
};

export default React.memo(AsyncAutocomplete);
